/**
 * Capability Management Page
 * Admin interface for managing AI capabilities with DataGrid
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Pencil, Trash2, RefreshCcw, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useCapabilities,
  useUpdateCapability,
  useDeleteCapability,
  useToggleCapabilityStatus,
} from '../../services/capability.service';
import { Capability, CapabilityFormValues } from '../../types/admin';
import { EmptyState } from '../../components/admin/EmptyState';

// ============================================
// Form Dialog Component
// ============================================

interface CapabilityFormDialogProps {
  open: boolean;
  onClose: () => void;
  capability?: Capability | null;
}

const CapabilityFormDialog: React.FC<CapabilityFormDialogProps> = ({
  open,
  onClose,
  capability,
}) => {
  const updateMutation = useUpdateCapability();

  const [formValues, setFormValues] = useState<CapabilityFormValues>({
    name: capability?.name || '',
    description: capability?.description || '',
    code: capability?.code || '',
    configSchema: capability?.configSchema || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof CapabilityFormValues, string>>>({});

  // Update form when capability changes
  React.useEffect(() => {
    if (capability) {
      setFormValues({
        name: capability.name,
        description: capability.description || '',
        code: capability.code,
        configSchema: capability.configSchema || '',
      });
    } else {
      setFormValues({
        name: '',
        description: '',
        code: '',
        configSchema: '',
      });
    }
    setErrors({});
  }, [capability, open]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CapabilityFormValues, string>> = {};

    if (!formValues.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formValues.code.trim()) {
      newErrors.code = 'Code is required';
    } else if (!/^[a-z0-9_]+$/.test(formValues.code)) {
      newErrors.code = 'Code must be lowercase letters, numbers, and underscores only';
    }

    if (formValues.configSchema && formValues.configSchema.trim()) {
      try {
        JSON.parse(formValues.configSchema);
      } catch {
        newErrors.configSchema = 'Config schema must be valid JSON';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!capability) {
      return;
    }

    const data = {
      name: formValues.name.trim(),
      description: formValues.description?.trim() || undefined,
      code: formValues.code.trim(),
      configSchema: formValues.configSchema?.trim() || undefined,
    };

    try {
      await updateMutation.mutateAsync({
        publicId: capability.publicId,
        ...data,
      });
      onClose();
    } catch (error) {
      // Error handled by mutation hooks
      console.error('Form submission error:', error);
    }
  };

  const handleChange = (field: keyof CapabilityFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isLoading = updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className="bg-slate-50 dark:bg-slate-800">
        <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
          Edit Capability
        </Typography>
      </DialogTitle>

      <DialogContent className="mt-4">
        <Box className="space-y-4">
          {/* Name Field */}
          <TextField
            fullWidth
            label="Name"
            value={formValues.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name || 'Display name for the capability'}
            disabled={isLoading}
            required
          />

          {/* Description Field */}
          <TextField
            fullWidth
            label="Description"
            value={formValues.description}
            onChange={(e) => handleChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description || 'Brief description of what this capability does'}
            disabled={isLoading}
            multiline
            rows={2}
          />

          {/* Code Field */}
          <TextField
            fullWidth
            label="Code"
            value={formValues.code}
            onChange={(e) => handleChange('code', e.target.value)}
            error={!!errors.code}
            helperText={errors.code || 'Unique identifier (e.g., text_generate, text_to_image)'}
            disabled={isLoading}
            required
            InputProps={{
              style: { fontFamily: 'monospace' },
            }}
          />

          {/* Config Schema Field */}
          <TextField
            fullWidth
            label="Config Schema (JSON)"
            value={formValues.configSchema}
            onChange={(e) => handleChange('configSchema', e.target.value)}
            error={!!errors.configSchema}
            helperText={errors.configSchema || 'Optional JSON configuration schema'}
            disabled={isLoading}
            multiline
            rows={4}
            inputProps={{
              style: { fontFamily: 'monospace', fontSize: '0.875rem' },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions className="px-6 pb-4">
        <Button
          onClick={onClose}
          disabled={isLoading}
          className="text-gray-600 dark:text-slate-400"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          className="bg-blue-600 text-white hover:bg-blue-700"
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {isLoading ? 'Saving...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================
// Capability Management Component
// ============================================

export const CapabilityManagement: React.FC = () => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCapability, setSelectedCapability] = useState<Capability | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const {
    data: capabilityPage,
    isLoading,
    isFetching,
    error,
  } = useCapabilities({
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
  });
  const deleteMutation = useDeleteCapability();
  const toggleStatusMutation = useToggleCapabilityStatus();
  const capabilities = capabilityPage?.data ?? [];
  const totalCapabilities = capabilityPage?.meta.total ?? 0;

  // Column definitions
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 220,
      renderCell: (params) => {
        const description = params.row.description;

        return (
          <Box className="min-w-0">
            <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
              {params.value}
            </Typography>
            {description && (
              <Typography
                variant="caption"
                className="block truncate text-gray-500 dark:text-slate-400"
                title={description}
              >
                {description}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          className="truncate font-mono text-sm text-gray-700 dark:text-slate-300"
          title={params.value}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'modelCount',
      headerName: 'Models',
      width: 96,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" className="font-semibold text-gray-900 dark:text-slate-100">
          {params.value ?? 0}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 112,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={(e) => {
            toggleStatusMutation.mutate({
              publicId: params.row.publicId,
              isActive: e.target.checked,
            });
          }}
          disabled={toggleStatusMutation.isPending}
          className="text-blue-600"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 132,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => (
        <Box className="flex w-full justify-end gap-1">
          <IconButton
            size="small"
            onClick={() => navigate(`/capabilities/${params.row.publicId}`)}
            className="text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-900/20"
            title="View Details"
          >
            <Eye size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedCapability(params.row as Capability);
              setDialogOpen(true);
            }}
            className="text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
            title="Edit"
          >
            <Pencil size={16} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setDeleteConfirmId(params.row.publicId)}
            className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
            title="Delete"
          >
            <Trash2 size={16} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCapability(null);
  };

  const handleDelete = async () => {
    if (deleteConfirmId) {
      await deleteMutation.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
    }
  };

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* Header */}
      <Box className="mb-6">
        <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
          Capability Management
        </Typography>
        <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
          Manage AI capabilities and their execution patterns
        </Typography>
      </Box>

      {/* Table Card */}
      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        {/* Toolbar */}
        <Box className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-slate-700">
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Capabilities
          </Typography>
        </Box>

        {/* DataGrid */}
        {isLoading ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className="p-12">
            <Typography className="text-center text-red-600 dark:text-red-400">
              Failed to load capabilities. Please try again.
            </Typography>
          </Box>
        ) : totalCapabilities > 0 ? (
          <DataGrid
            rows={capabilities}
            columns={columns}
            getRowId={(row) => row.publicId}
            rowCount={totalCapabilities}
            loading={isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[5, 10, 25, 50]}
            disableRowSelectionOnClick
            autoHeight
            className="border-0"
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
            }}
          />
        ) : (
          <Box className="p-12">
            <EmptyState
              Icon={RefreshCcw}
              title="No Capabilities"
              description="No system capabilities are available."
            />
          </Box>
        )}
      </Box>

      {/* Form Dialog */}
      <CapabilityFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        capability={selectedCapability}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className="bg-red-50 dark:bg-red-900/20">
          <Typography variant="h6" className="font-semibold text-red-900 dark:text-red-400">
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent className="mt-4">
          <Typography className="text-gray-700 dark:text-slate-300">
            Are you sure you want to delete this capability? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button
            onClick={() => setDeleteConfirmId(null)}
            disabled={deleteMutation.isPending}
            className="text-gray-600 dark:text-slate-400"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={deleteMutation.isPending}
            className="bg-red-600 text-white hover:bg-red-700"
            startIcon={
              deleteMutation.isPending ? <CircularProgress size={16} /> : <Trash2 size={16} />
            }
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
